
import {Container as PixiContainer, DisplayObject, Point, Texture, Rectangle} from 'pixi.js';
import {PointLike, RectangleLike, CleanComponentProps, EventMap, EventCallbacks, CanBeSignal, CanBeSignals, ComponentRef} from './types';
import {Store, UnwrapCanBeSignal, ComputedSignal} from '@gongxigames/framework/core/fragments/store/Store';
import {signal, Signal} from '@preact/signals-core';
import {coreStore} from '@gongxigames/framework/core/globals';
import {ResponsiveConf} from '@gongxigames/framework/core/fragments/display/responsive';

/**
 * Proper iterator for child `JSX.Elements` of a Component.
 * It can handle single elements, arrays of elements,
 * and signals with elements, and it skips `undefined` values if some.
 * Components generate them.
 */
export function* iterChildren<T extends any = PixiContainer<DisplayObject>>(children: any): Generator<T, void> {
    if (Array.isArray(children)) {
        for (const child of children) {
            yield* iterChildren(child) as any;
        }

        return;
    }

    if (Store.isSignal(children)) {
        yield* iterChildren(children.value) as any;

        return;
    }

    if (children !== undefined) {
        yield children as any;
    }
}

export function copyPointData(data: PointLike, point: Point): void {
    if (Array.isArray(data)) {
        point.set(data[0], data[1]);

        return;
    }

    if (typeof data === 'number') {
        point.set(data);

        return;
    }

    point.copyFrom(data);
}

export function copyRectangleData(data: RectangleLike, rectangle: Rectangle): void {
    const isArray = Array.isArray(data);

    if (isArray && data.length === 2) {
        rectangle.width = data[0];
        rectangle.height = data[1];
        rectangle.x = -rectangle.width / 2;
        rectangle.y = -rectangle.height / 2;

        return;
    }

    if (isArray && data.length === 4) {
        rectangle.x = data[0];
        rectangle.y = data[1];
        rectangle.width = data[2];
        rectangle.height = data[3];

        return;
    }

    if (typeof data === 'number') {
        rectangle.x = rectangle.y = -data / 2;
        rectangle.width = rectangle.height = data;

        return;
    }

    rectangle.copyFrom(data);
}

function swapItems(arr: any[], i1: number, i2: number): void {
    if (i1 === i2) {
        return;
    };

    const saved = arr[i1];

    arr[i1] = arr[i2];
    arr[i2] = saved;
}

type MapSignalArrayCB<T, R> = (item: T, index: Signal<number>, arr: T[]) => R;
type ForEachMissingCB<T> = (item: T, arr1: T[], arr2: T[]) => void;

/**
 * This method calls a callback function
 * for each element in array `items`
 * that is not in array `arrayToSearchIn`
 * @param items - array of item to operate on
 * @param arrayToSearchIn - array of item to check items is exists in;
 * @param cb - callback function for each item
 */
function forEachMissing<T>(items: T[], arrayToSearchIn: T[], cb: ForEachMissingCB<T>) {
    items.forEach(item => {
        if (!arrayToSearchIn.includes(item)) {
            cb(item, items, arrayToSearchIn);
        }
    });
}

type MapSignalArrayCache<T, R> = [T, R, Signal<number>];
type MapSignalArrayForMissingCB<T, R> = (inputData: T, outputData: R) => void;

/**
 * This function maps an array of objects,
 * it guarantees that the mapping function
 * will be called only once
 * for each new element.
 *
 * It also accepts an optional cleanup function
 * which also guarantees a single call
 * for each element that has been removed
 * from this array.
 *
 * @param arraySignal - input array of data
 * @param cb - function for array items processing, Runs only once per item
 * @param cbForRemoved - optional function to handle cleanup for removed items
 * @returns Signal<T[]> - array of processed data
 */
export function mapSignalArray<T, R>(
    arraySignal: Signal<T[]>,
    cb: MapSignalArrayCB<T, R>,
    cbForRemoved?: MapSignalArrayForMissingCB<T, R>
): Signal<R[]> {
    let cacheResults: MapSignalArrayCache<T, R>[] = [];

    const result = Store.computed(() => {
        const cacheResultsNew: MapSignalArrayCache<T, R>[] = [];
        const array = arraySignal.value;
        let successIndex = 0;

        const result = array.map((item, index) => {
            let i;

            for (i = successIndex; i < cacheResults.length; i++) {
                if(cacheResults[i][0] === item) {
                    swapItems(cacheResults, successIndex, i);
                    successIndex++;
                    break;
                }
            }

            if (cacheResults.length !== i) {
                const cache = cacheResults[successIndex - 1];

                cacheResultsNew.push(cache);
                cache[2].value = index;
            } else {
                const indexSignal = signal(index);

                cacheResultsNew.push([item, cb(item, indexSignal, array), indexSignal]);
            }

            return cacheResultsNew[cacheResultsNew.length - 1][1];
        });

        if (cbForRemoved) {
            forEachMissing(cacheResults as any[], cacheResultsNew as any[], item => cbForRemoved(item[0], item[1]));
        }

        cacheResults = cacheResultsNew;

        return result;
    });

    return result;
}

/**
 * Crate a Proxy for key value store,
 * The Proxy will unwrap values
 * fom Signals in a kay value store
 */
export function createPropsGetter<T extends {}>(props: T): CleanComponentProps<T> {
    return new Proxy(props, {
        get(target, key) {
            return getProp((target as any)[key]);
        },

        set() {
            throw new Error('Component props are read only.');
        }
    }) as any;
}

/**
 * Return value of a Signal if value is a signal,
 * otherwise just returns the value.
 * Works well with `CanBeSignal`, `CanBeSignals`.
 */
export function getProp<T, D extends UnwrapCanBeSignal<T>>(val: T, defaultResult: D): D;
export function getProp<T>(val: T): UnwrapCanBeSignal<T>;
export function getProp<T, D extends UnwrapCanBeSignal<T>>(val: T, defaultResult?: D): UnwrapCanBeSignal<T> {
    if (Store.isSignal(val)) {
        if (val.value === undefined) {
            return defaultResult as any;
        } else {
            return val.value as any;
        }
    }

    if (val === undefined) {
        return defaultResult as any;
    } else {
        return val as any;
    }
}

/**
 * Return value of a Signal if value is a signal,
 * but do not subscribe to changes,
 * if value is not a signal just returns the value.
 * Works well with `CanBeSignal`, `CanBeSignals`.
 */
export function peekProp<T, D extends UnwrapCanBeSignal<T>>(val: T, defaultResult: D): D;
export function peekProp<T>(val: T): UnwrapCanBeSignal<T>;
export function peekProp<T, D extends UnwrapCanBeSignal<T>>(val: T, defaultResult?: D): UnwrapCanBeSignal<T> {
    if (Store.isSignal(val)) {
        if (val.peek === undefined) {
            return defaultResult as any;
        } else {
            return val.peek as any;
        }
    }

    if (val === undefined) {
        return defaultResult as any;
    } else {
        return val as any;
    }
}

/**
 * Convert signal type to another computed type.
 * For example:
 * ```ts
 * const winAmount = Store.signal(100);
 * const isWin = mapProp(winAmount, Boolean);
 * const isNoWin = mapProp(winAmount, val => !!val);
 * const winFormatted = mapProp(winAmount, win => `$ ${win}`);
 * ```
 */
export function mapProp<T, D>(val: T, castFn: (data: UnwrapCanBeSignal<T>) => D): ComputedSignal<D> {
    return Store.computed(() => castFn(getProp(val)));
}

/**
 * @deprecated
 */
export function castProp<T, D>(val: T, castFn: (data: UnwrapCanBeSignal<T>) => D): ComputedSignal<D> {
    throw new Error('castProp renamed to mapSignal');
}

/**
 * Splits copy 2 new copies of a `Record`,
 * but the first object contains only the specified fields,
 * and the second contains all remaining fields.
 */
export function splitProps<
    T extends {},
    K extends keyof T,
    R1 extends Pick<T, K>,
    R2 extends Omit<T, K>,
>(props: T, keys: K[]): [R1, R2] {
    const result1 = {} as R1;
    const result2 = {} as R2;

    Object
        .keys(props)
        .forEach(key => {
            const isInList = keys.includes(key as K);
            const val = props[key as K];

            isInList ?
                (result1 as any)[key] = val:
                (result2 as any)[key] = val;
        });

    return [result1, result2];
}

export function destructProp<T, K extends keyof T>(value: CanBeSignal<T>, keys: K[]): {[key in K]: ComputedSignal<T[key]>} {
    const result = {} as {[key in K]: ComputedSignal<T[key]>};

    for (const key of keys) {
        result[key] = Store.computed(() => {
            const signalValue = getProp(value) as T;

            return signalValue[key];
        });
    }

    return result;
}

/**
 * Add new events to existing pixi object events.
 */
function patchEventsSimple<
    T extends EventMap,
    P extends Partial<T>,
>(events: T, patch: P): void {
    for (const key in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
            let currentEvents: EventCallbacks<any> = events[key];
            const newEvents: EventCallbacks<any> = patch[key];

            if (newEvents === undefined) {
                continue;
            }

            if (!currentEvents) {
                currentEvents = [];
            } else if (!Array.isArray(currentEvents)) {
                currentEvents = [currentEvents];
            }

            if (Array.isArray(newEvents)) {
                (currentEvents as any[]).push(...newEvents);
            } else {
                (currentEvents as any[]).push(newEvents);
            }

            events[key] = currentEvents;
        }
    }
}

/**
 * Create a computed that will add new events
 * to the existing PIXI object events.
 */
export function patchEvents<
    E extends CanBeSignal<{}>,
    P extends Partial<UnwrapCanBeSignal<E>>,
>(eventsSignal: E, patch: P): void {
    if (Store.isSignal(eventsSignal)) {
        return Store.computed(() => {
            const events = getProp(eventsSignal);

            patchEventsSimple(events, patch);

            return {...events};
        }) as any;
    }

    patchEventsSimple(eventsSignal, patch);
}

/**
 * Get a texture from a pool of loaded textures or crete it from data URL.
 */
export function getTexture(texture: string | Texture): Texture {
    const isString = typeof texture === 'string';
    const isDataURL = isString && texture.startsWith('data:image/');

    if (!isString) {
        return texture;
    }

    if (isDataURL) {
        return Texture.from(texture);
    }

    return coreStore
        .resourceLoader
        .value!
        .getTexture(texture);
}

type ResponsiveProps = CanBeSignals<{
    responsive?: ResponsiveConf;
}>;

export function responsiveSkipChildren(props: ResponsiveProps): void {
    const responsive = props.responsive;

    props.responsive = Store.computed(() => {
        const result: ResponsiveConf = {
            ...getProp(responsive, {}),
            skipChildren: true,
        };

        return result;
    });
}

export function handleRef<T>(instance: T, ref?: ComponentRef<T>): void {
    if (Store.isSignal(ref)) {
        ref.value = instance;

        return;
    }

    ref?.(instance);
}